// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { DashboardLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import AxiosInstance from "@/utils/AxiosInstance";
import { toast } from "sonner";
import { formatDateTime12, formatDateTime24 } from "@/utils/helpers";
import LoaderWrapper from "@/pages/LoaderWrapper";

export default function SupportTicketDetails() {
  const { id } = useParams();
  const [ticket, setTicket] = useState<any>(null);
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [ticketErr, setTicketErr] = useState<any>(null);

  const patient = ticket?.raw?.patient;
  const [noteText, setNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const notes = useMemo(() => {
    const n = ticket?.supportTicketNotes;
    return Array.isArray(n) ? n?.reverse() : [];
  }, [ticket]);

  const fetchTicket = async () => {
    if (!id) return;
    setIsLoadingTicket(true);
    setTicketErr(null);
    try {
      const res = await AxiosInstance().get(`/impilo/support-tickets/${id}`);
      const apiTicket = res?.data?.data ?? res?.data?.ticket ?? res?.data;
      setTicket(apiTicket || null);
    } catch (e: any) {
      setTicketErr(e);
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load ticket",
      );
      setTicket(null);
    } finally {
      setIsLoadingTicket(false);
    }
  };

  useEffect(() => {
    if(id) {
    fetchTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // console.log({notes});

  const handleAddNote = async () => {
    const trimmed = String(noteText || "").trim();
    if (!trimmed) {
      toast.error("Please enter a note.");
      return;
    }
    if (!id) {
      toast.error("Ticket ID is missing.");
      return;
    }
    const ticketId = ticket?.ticketId;

    if (!ticketId) {
      toast.error("Ticket ID is missing.");
      return;
    }

    setIsSaving(true);
    try {
      await AxiosInstance().post(`/impilo/support-tickets/${ticketId}/note`, {
        note: trimmed,
      });
      setNoteText("");
      toast.success("Note added.");
      await fetchTicket();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to add note",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout activePage="/support">
      <LoaderWrapper
        loading={isLoadingTicket}
        error={ticketErr}
      >
        <div className="p-6 space-y-4">
        <div>
          <h1 className="text-xl font-semibold">Support Ticket</h1>
        </div>

        <div className="rounded-lg border bg-white overflow-hidden">
          <div className="p-4">
            <div className="text-base font-semibold">Ticket Information</div>
          </div>
          <Separator />

          <div className="p-4">
            <div className="grid grid-cols-1 gap-y-6 gap-x-10 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Patient</div>
                <div className="text-sm">
                  <span className="underline underline-offset-2">
                    {[patient?.firstName, patient?.lastName]
                      .filter(Boolean)
                      .join(" ") || "—"}
                    {(() => {
                      const orgName =
                        ticket?.organization?.name ||
                        ticket?.organizationName ||
                        ticket?.orgName ||
                        patient?.site?.name ||
                        ticket?.site?.name ||
                        patient?.site ||
                        ticket?.site;
                      return orgName ? ` (${orgName})` : "";
                    })()}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Ticket Type</div>
                <div className="text-sm">
                  {ticket?.ticketType ||
                    ticket?.type ||
                    ticket?.category ||
                    "—"}
                </div>
              </div>
{/* 
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Status</div>
                <div className="text-sm">{ticket?.currentStatus || "—"}</div>
              </div> */}

              {/* <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Priority</div>
                <div className="text-sm">{ticket?.priority || "Not Specified"}</div>
              </div> */}

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Email</div>
                <div className="text-sm">{patient?.email || ticket?.email || "—"}</div>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Phone Number</div>
                <div className="text-sm">
                  {patient?.phoneNumber || ticket?.phoneNumber || "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Created By</div>
                <div className="text-sm">
                  {ticket?.createdByUser?.name ||
                    [ticket?.createdBy?.firstName, ticket?.createdBy?.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    ticket?.createdByName ||
                    ticket?.authorName ||
                    "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Date of Birth</div>
                <div className="text-sm">{patient?.dateOfBirth || "—"}</div>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Customer Name</div>
                <div className="text-sm">{ticket?.customer?.name || "—"}</div>
              </div>
            </div>

            <div className="mt-4 text-xs text-muted-foreground">
              Ticket ID: <span className="font-medium text-foreground">{ticket?.ticketId || "—"}</span>
              {" • "}
              Patient ID:{" "}
              <span className="font-medium text-foreground">
                {patient?.externalIdentifier || patient?.id || "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white overflow-hidden">
          <div className="p-4">
            <div className="text-base font-semibold">Conversation</div>
          </div>
          <Separator />


          <div className="p-4 space-y-3">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note"
              rows={3}
            />
            <div className="flex justify-start">
              <Button onClick={handleAddNote} loading={isSaving} disabled={isSaving || !noteText.trim()}>
                Save Note
              </Button>
            </div>
          </div>

          <Separator />
          <div className="max-h-[520px] overflow-y-auto">
            {notes?.length ? (
              <div className="divide-y">
                {notes.map((n: any, idx: number) => {
                  const author =
                    n?.createdByUser
                      ? `${n?.createdByUser?.name || ""}`
                      : n?.authorName || "—";

                  return (
                    <div key={`${n?.createdAt || ""}-${idx}`} className="p-4">
                      <div className="text-sm font-medium text-foreground">
                        {author || "—"}
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap wrap-break-word">
                        {n?.note || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        {formatDateTime12(n?.createdAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-sm text-muted-foreground">
                No conversation yet.
              </div>
            )}
          </div>

          {/* <Separator /> */}
        </div>
      </div>
      </LoaderWrapper>
    </DashboardLayout>
  );
}

